const KEY = 'henry-view-museum-v1';
const storage = () => { try { return typeof window === 'undefined' ? null : window.sessionStorage; } catch { return null; } };
export function museumSession(store = storage()) {
  try {
    const value = JSON.parse(store?.getItem(KEY) || '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch { return {}; }
}
export function updateMuseumSession(patch, store = storage()) {
  const next = { ...museumSession(store), ...patch };
  try { store?.setItem(KEY, JSON.stringify(next)); } catch { /* The visit also works without storage. */ }
  return next;
}
export const ARTICLE_DEFAULT_QUERY = '(max-width: 600px) and (orientation: portrait)';
export function prefersMuseum(store = storage(), viewport = typeof window === 'undefined' ? null : window) {
  const mode = museumSession(store).mode;
  if (mode === 'article' || mode === 'museum') return mode === 'museum';
  return !viewport?.matchMedia?.(ARTICLE_DEFAULT_QUERY).matches;
}
export function enterMuseumFromGallery(store = storage()) {
  return updateMuseumSession({ mode: 'museum', resumeOnViews: false }, store);
}
function notifyVisitChange() {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('museum-visit-updated'));
}
export function rememberViewVisit(id, title, position, store=storage()) {
  if (!id) return;
  const state = museumSession(store);
  const visits = { ...(state.visits || {}), [id]: { id, title, position, ...(state.visits?.[id]?.completedAt ? {completedAt:state.visits[id].completedAt} : {}) } };
  updateMuseumSession({ lastView: { id, title, position }, visits, mode: 'museum' },store);
  notifyVisitChange();
}
export function museumReturnPosition(id,store=storage()) {
  const state = museumSession(store);
  if (state.visits?.[id]?.completed || (state.lastView?.id === id && state.lastView.completed)) return null;
  return state.visits?.[id]?.position ?? (state.lastView?.id === id ? state.lastView.position : null);
}
export function completeViewVisit(id, store=storage()) {
  if (!id) return;
  const state = museumSession(store);
  const visit = state.visits?.[id] || (state.lastView?.id === id ? state.lastView : { id });
  const completed = { ...visit, completed:true, completedAt:visit.completedAt || Date.now() };
  updateMuseumSession({ visits:{...state.visits,[id]:completed},
    lastView:state.lastView?.id===id?completed:state.lastView,
    completedViews:[...new Set([...(state.completedViews||[]),id])] },store);
  notifyVisitChange();
}
export function consumeMuseumResume(store = storage()) {
  const state = museumSession(store);
  updateMuseumSession({ resumeOnViews: false }, store);
  return state.mode !== 'article' && state.resumeOnViews && !state.lastView?.completed && /^[\w-]+$/.test(state.lastView?.id || '') ? state.lastView : null;
}

export function museumNextAction(selected, chosenId, lastView, {completedViews=[],ownViews=[],hasVisitHistory=false,galleryTourSeen=false} = {}) {
  const chosen = selected?.id && selected.id === chosenId ? selected : null;
  if (lastView?.id && !lastView.completed && Number.isInteger(lastView.position?.stepIndex) && lastView.position.stepIndex >= 0 && (!chosen || chosen.id === lastView.id)) {
    return { kind: 'resume', label: 'Continue your visit', view: lastView };
  }
  if (chosen && !completedViews.includes(chosen.id)) return { kind: 'enter', label: 'Visit this View', view: chosen };
  if (completedViews.length || lastView?.completed) {
    if (!ownViews.length) return { kind:'create', label:'Create your own View', view:null };
    return { kind:'explore', label:'Explore another exhibition', view:null };
  }
  const unvisitedOwnView = ownViews.find(view => !completedViews.includes(view.id));
  if (unvisitedOwnView) return { kind:'enter', label:'Visit your View', view:unvisitedOwnView };
  if (!hasVisitHistory && !galleryTourSeen) return { kind: 'tour', label: 'Take a short tour', view: null };
  return { kind: 'explore', label: 'Explore the exhibitions', view: null };
}

// This small catalogue keeps links and presentation metadata. Ownership tokens
// and the preserved source snapshots remain in their existing stores.
export function rememberOwnView(view, store = storage()) {
  if (!/^[\w-]+$/.test(view?.id || '')) return;
  const stored = museumSession(store).ownViews;
  const previous = Array.isArray(stored) ? stored : [];
  const existing = previous.find(item => item.id === view.id) || {};
  const entry = { ...existing };
  for (const key of ['id', 'title', 'question', 'illustration', 'created_at', 'visibility']) {
    if (view[key] !== undefined) entry[key] = view[key];
  }
  updateMuseumSession({ ownViews: [entry, ...previous.filter(item => item.id !== entry.id)] }, store);
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('museum-views-updated'));
}
export function ownMuseumViews(store = storage()) {
  const views = museumSession(store).ownViews;
  return Array.isArray(views) ? views.filter(view => /^[\w-]+$/.test(view?.id || '') && view.title) : [];
}
export function isMuseumRoute(path) {
  return /^\/(?:views|exhibition-showroom|view(?:\/[\w-]+)?)(?:[?#]|$)/.test(String(path || ''));
}
export function rememberMuseumOrigin(origin, store = storage()) {
  if (!origin?.route?.startsWith('/') || origin.route.startsWith('//') || isMuseumRoute(origin.route)) return;
  updateMuseumSession({ origin }, store);
}
export function museumOrigin(store = storage()) {
  const origin = museumSession(store).origin;
  return origin?.route?.startsWith('/') && !origin.route.startsWith('//') && !isMuseumRoute(origin.route) ? origin : null;
}

export function hasExhibitionFeedback(id, store=storage()) {
  return Boolean(museumSession(store).feedbackSubmitted?.[id]);
}
export function rememberExhibitionFeedback(id, store=storage()) {
  const state=museumSession(store);
  updateMuseumSession({feedbackSubmitted:{...state.feedbackSubmitted,[id]:Date.now()}},store);
}
